/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import React from 'react';
import type {
  DayOpeningHours,
  DepartmentOpeningHours,
  PbcDataPartner,
  TimeRange,
} from './PartnerData';
import type { GfaLocaleServiceV1 } from '../feature-service-types';
import { getHost } from '../../lib/utils';

interface JsonLdProps {
  readonly data: PbcDataPartner;
  jsonLdType: string;
  socialMedia: SocialMediaItem[];
  localeService: GfaLocaleServiceV1;
}

type SocialPlatform = 'facebook' | 'instagram' | 'twitter' | 'linkedln' | 'youtube' | 'vimeo';

type SocialMediaItem = {
  [K in SocialPlatform]: boolean;
} & {
  [K in `${SocialPlatform}Url`]?: string;
};

type JsonValue =
  | string
  | number
  | boolean
  | JsonValue[]
  | {
      [key: string]: JsonValue;
    };

const platforms: SocialPlatform[] = [
  'facebook',
  'instagram',
  'twitter',
  'linkedln',
  'youtube',
  'vimeo',
];

/** Returns the value only if it is a non-empty, non-null string; otherwise undefined (omitted from JSON). */
const defined = (value: string | null | undefined): string | undefined =>
  value != null && value.trim() !== '' ? value : undefined;

const getName = (data: PbcDataPartner): string | undefined =>
  defined(data?.additionalData?.displayName) ||
  defined(data.name) ||
  defined(data?.officialName) ||
  defined(data?.nonOfficialName);

const isValidTimeRange = (timeRange: TimeRange | undefined): boolean => {
  const openTime = defined(timeRange?.openTime);
  const closeTime = defined(timeRange?.closeTime);
  return openTime != null && closeTime != null;
};

const withoutMissingFields = (value: unknown): JsonValue | undefined => {
  if (value == null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const cleanedItems = value
      .map((item) => withoutMissingFields(item))
      .filter((item): item is JsonValue => item !== undefined);

    return cleanedItems.length ? cleanedItems : undefined;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, item]) => [key, withoutMissingFields(item)] as const)
      .filter((entry): entry is readonly [string, JsonValue] => entry[1] !== undefined);

    return entries.length ? Object.fromEntries(entries) : undefined;
  }

  return value as JsonValue;
};

/** Parses line 2 of an address, e.g. "Rancho Mirage, CA 92270" or "01796 Pirna". */

/** Maps open day to OpeningHoursSpecification entry using first time range (if any). */
const toOpeningHoursSpec = (item: DayOpeningHours): JsonValue | undefined => {
  if (!item.open) return undefined;

  const validTimeRanges = item.timeRanges?.filter(isValidTimeRange);
  if (!validTimeRanges?.length) return undefined;

  // Use first time range; schema.org doesn't support multiple ranges per day
  const first = validTimeRanges[0];
  const opens = defined(first.openTime);
  const closes = defined(first.closeTime);

  if (!opens || !closes) return undefined;

  return {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: `https://schema.org/${item.id.charAt(0).toUpperCase() + item.id.slice(1)}`,
    opens,
    closes,
  };
};

export const JsonLd: React.FunctionComponent<JsonLdProps> = ({
  data,
  jsonLdType,
  socialMedia,
  localeService,
}) => {
  const { countryCode, language } = localeService;

  const buildAddress = () => ({
    '@type': 'PostalAddress',
    addressCountry: defined(data.country) ?? countryCode,
    postalCode: defined(data.zipCode),
    streetAddress: defined(data.street),
    addressLocality: defined(data.city),
    addressRegion: defined(data.region),
  });

  const openingHoursDepartments = data?.openingHours?.departments
    ?.map((department: DepartmentOpeningHours) => {
      const openDays = department?.openingHours
        ?.map((item) => toOpeningHoursSpec(item))
        .filter((item): item is JsonValue => item !== undefined);
      return {
        '@type': jsonLdType,
        name: department?.departmentName,
        ...(data?.additionalData?.locationImage && { image: data.additionalData.locationImage }),
        address: buildAddress(),
        ...(openDays?.length && { openingHoursSpecification: openDays }),
      };
    })
    .filter((dept) => dept.name);

  const socialMediaLinks: string[] = socialMedia.flatMap((media) =>
    platforms.flatMap((platform) => {
      const url = media[`${platform}Url`];
      return media[platform] && url ? [url] : [];
    }),
  );

  const phone = defined(data?.phone);
  const email = defined(data?.email);
  const locationImage = defined(data?.additionalData?.locationImage);
  const partnerUrl = data?.url ? `${getHost(data.url)}/${language}/` : undefined;
  const openDays = data?.openingHours?.departments?.[0]?.openingHours
    ?.map((item) => toOpeningHoursSpec(item))
    .filter((item): item is JsonValue => item !== undefined);

  const hasGeo = data?.latitude != null && data?.longitude != null;
  const contactPointFields = {
    ...(phone && { telephone: phone }),
    ...(email && { email }),
  };
  const hasContactPoint = phone != null || email != null;

  const jsonlddata = {
    '@context': 'https://schema.org',
    '@type': jsonLdType,
    ...(partnerUrl && { '@id': partnerUrl }),
    address: buildAddress(),
    brand: 'Audi',
    ...(hasContactPoint && {
      contactPoint: {
        '@type': 'ContactPoint',
        ...contactPointFields,
        contactType: 'Customer Service',
      },
    }),
    ...(openingHoursDepartments?.length && { department: openingHoursDepartments }),
    ...(hasGeo && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: data.latitude,
        longitude: data.longitude,
      },
    }),
    ...(locationImage && { image: locationImage }),
    ...(locationImage && { logo: locationImage }),
    name: getName(data),
    ...(openDays?.length && { openingHoursSpecification: openDays }),
    ...(socialMediaLinks.length && { sameAs: socialMediaLinks }),
    ...(phone && { telephone: phone }),
    ...(partnerUrl && { url: partnerUrl }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(withoutMissingFields(jsonlddata)),
      }}
    />
  );
};
